// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2024 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import mergeToNew from "@insite/client-framework/Common/mergeToNew";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { getSettingsCollection } from "@insite/client-framework/Store/Context/ContextSelectors";
import { ImageModel, ProductModel } from "@insite/client-framework/Types/ApiModels";
import { ClickablePresentationProps } from "@insite/mobius/Clickable";
import Img, { ImgProps } from "@insite/mobius/Img";
import { LazyImageProps } from "@insite/mobius/LazyImage";
import { LoadingSpinnerProps } from "@insite/mobius/LoadingSpinner";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import useElementDimensions from "@steiner/App/Hooks/UseElementDimensions";
import { CustomAppState } from "@steiner/App/Store/Reducers";
import breakpoints from "@steiner/XDL/PreStyle/breakpoints";
import React, { useEffect, useState } from "react";
import { connect } from "react-redux";
import { css } from "styled-components";
import { EasyZoomOnHover, enableScroll } from "easy-magnify";

interface OwnProps {
    product: ProductModel;
    image: ImageModel;
    useLargeImage?: boolean;
    onClick?: () => void;
    extendedStyles?: ProductPrimaryImageStyles;
    // XNG-Changes
    useZoom?: boolean;
}

type Props = OwnProps & ReturnType<typeof mapStateToProps>;

const mapStateToProps = (state: CustomAppState) => ({
    productSettings: getSettingsCollection(state).productSettings,
    // XNG-Changes
    isTabletScreen: state.xCommon?.isTabletScreen,
});

export interface ProductPrimaryImageStyles {
    centeringWrapper?: InjectableCss;
    /**
     * @deprecated Not used anymore
     */
    hiddenWrapper?: InjectableCss;
    /**
     * @deprecated Not used anymore
     */
    spinner?: LoadingSpinnerProps;
    /**
     * @deprecated Use img instead
     */
    image?: LazyImageProps;
    img?: ImgProps;
    // XNG-Changes
    enlargeBtn?: ClickablePresentationProps;
    container?: InjectableCss;
    zoomContainerStyles?: InjectableCss;
}

export const productPrimaryImageStyles: ProductPrimaryImageStyles = {
    centeringWrapper: {
        css: css`
            min-height: 300px;
            margin: 10px 0;
            display: flex;
            align-items: center;
            justify-content: center;
            img {
                height: auto;
                max-width: 100%;
            }
        `,
    },
    image: {
        css: css`
            margin: auto;
            cursor: pointer;
        `,
    },
    img: {
        css: css`
            cursor: pointer;
        `,
    },
};

const ProductPrimaryImage = ({
    productSettings,
    product,
    image,
    useLargeImage,
    onClick,
    extendedStyles,
    // XNG-Changes
    useZoom,
    isTabletScreen,
}: Props) => {
    const [styles] = useState(() => mergeToNew(productPrimaryImageStyles, extendedStyles));
    const [zoomContainer, setZoomContainer] = useState({ width: 690, height: 420 });
    const [bodyRef, bodySize] = useElementDimensions<HTMLElement>();
    const [documentWidth, setDocumentWidth] = useState(0);

    if (!product) {
        return null;
    }

    /**
     * XNG-Changes
     * Assign bodyRef to document.body
     * By this step, we can calculate maxWidth for viewport
     */
    useEffect(() => {
        if (typeof window !== "undefined") {
            bodyRef.current = document.body;
        }
    }, []);

    useEffect(() => {
        if (bodySize?.width) {
            setDocumentWidth(bodySize.width);
        }
    }, [bodySize?.width]);

    useEffect(() => {
        if (documentWidth) {
            if (documentWidth >= breakpoints.maxWidths[3]) {
                setZoomContainer({ width: 690, height: 420 });
            } else {
                const newWidth = documentWidth - 484;
                setZoomContainer({ width: Math.floor(newWidth), height: Math.floor(newWidth * 0.77) });
            }
        }
    }, [documentWidth]);

    useEffect(() => {
        if ((window as any).sirvScriptAdded) {
            return;
        }

        if (
            productSettings.imageProvider !== "SIRV" ||
            !product.images ||
            product.images.every(o => o.imageType !== "360")
        ) {
            return;
        }

        const script = document.createElement("script");
        script.src = "https://scripts.sirv.com/sirv.js";
        script.async = true;

        document.body.appendChild(script);
        (window as any).sirvScriptAdded = true;
    }, [productSettings.imageProvider, product]);

    const imageWrapperClickHandler = () => {
        onClick?.();
    };

    const imageWrapperKeyHandler: React.KeyboardEventHandler<HTMLImageElement> = event => {
        event.preventDefault();
        event.stopPropagation();
        if (event.key === "Enter") {
            onClick?.();
        }
    };

    const path = useLargeImage
        ? image.largeImagePath || image.mediumImagePath
        : image.mediumImagePath || image.largeImagePath;
    if (!path) {
        return null;
    }

    /**
     * XNG-Changes
     * Change Structure
     * Support Zoom in
     */
    const hasImages = !!product?.images?.length;
    return (
        <StyledWrapper {...styles.container} data-test-selector="primary-image-container">
            {image.imageType === "Static" && (
                <StyledWrapper {...styles.centeringWrapper} onClick={imageWrapperClickHandler}>
                    {hasImages && useZoom && !isTabletScreen ? (
                        <StyledWrapper
                            {...styles.zoomContainerStyles}
                            {...{ width: zoomContainer.width }}
                            onMouseOver={() => enableScroll?.()}
                        >
                            <EasyZoomOnHover
                                mainImage={{
                                    src: image.mediumImagePath,
                                    alt: image.imageAltText,
                                    width: 430,
                                    height: 430,
                                }}
                                zoomImage={{
                                    src: image.largeImagePath,
                                    alt: image.imageAltText,
                                }}
                                zoomContainerWidth={zoomContainer.width}
                                zoomContainerHeight={zoomContainer.height}
                                delayTimer={0}
                                distance={20}
                                zoomLensScale={1}
                            />
                        </StyledWrapper>
                    ) : (
                        <Img
                            {...styles.img}
                            src={path}
                            altText={image.imageAltText}
                            data-test-selector="productDetails_mainImage"
                            onKeyPress={imageWrapperKeyHandler}
                            tabIndex={0}
                        />
                    )}
                </StyledWrapper>
            )}
            {image.imageType === "360" && productSettings.imageProvider === "SIRV" && (
                <div style={{ minHeight: 300 }} onClick={imageWrapperClickHandler} aria-label="image-zoom">
                    <div className="Sirv" key={image.id} data-src={image.mediumImagePath} />
                </div>
            )}
        </StyledWrapper>
    );
};

export default connect(mapStateToProps)(ProductPrimaryImage);
